import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import api from '../../services/api'
import { useAuthStore } from '../../stores/authStore'
import {
    Plus,
    Search,
    Filter,
    Eye,
    Edit,
    Trash2,
    ChevronLeft,
    ChevronRight,
    Loader2,
    X,
    Download
} from 'lucide-react'
import * as XLSX from 'xlsx'
import { format } from 'date-fns'
import { fr, enUS } from 'date-fns/locale'
import toast from 'react-hot-toast'
import './RendezvousListPage.css'

export default function RendezvousListPage() {
    const { t, i18n } = useTranslation()
    const navigate = useNavigate()
    const { hasPermission, user } = useAuthStore()
    const dateLocale = i18n.language === 'fr' ? fr : enUS

    const [rendezvous, setRendezvous] = useState([])
    const [loading, setLoading] = useState(true)
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 })
    const [filters, setFilters] = useState({
        search: '',
        etat: 'all',
        date_debut: '',
        date_fin: ''
    })
    const [showFilters, setShowFilters] = useState(false)
    const [exporting, setExporting] = useState(false)

    useEffect(() => {
        fetchRendezvous()
    }, [pagination.page, filters])

    const fetchRendezvous = async () => {
        setLoading(true)
        try {
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            }

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === 'all') {
                    delete params[key]
                }
            })

            const response = await api.get('/rendezvous', { params })
            setRendezvous(response.data.data.rendezvous)
            setPagination(prev => ({ ...prev, ...response.data.data.pagination }))
        } catch (error) {
            toast.error(t('common.error'))
        } finally {
            setLoading(false)
        }
    }

    const handleSearch = (e) => {
        e.preventDefault()
        setPagination(prev => ({ ...prev, page: 1 }))
        fetchRendezvous()
    }

    const handleDelete = async (id) => {
        if (!window.confirm(t('common.confirm') + '?')) return

        try {
            await api.delete(`/rendezvous/${id}`)
            toast.success(t('common.success'))
            fetchRendezvous()
        } catch (error) {
            toast.error(error.response?.data?.message || t('common.error'))
        }
    }

    const getStateBadgeClass = (state) => {
        const classes = {
            PLANIFIE: 'badge-info',
            QUALITE_DONE: 'badge-primary',
            DISPATCHED: 'badge-warning',
            CONSEILLER_DONE: 'badge-success',
            ANNULE: 'badge-error'
        }
        return classes[state] || 'badge-neutral'
    }

    const clearFilters = () => {
        setFilters({
            search: '',
            etat: 'all',
            date_debut: '',
            date_fin: ''
        })
        setPagination(prev => ({ ...prev, page: 1 }))
    }

    const handleExport = async () => {
        setExporting(true)
        try {
            const params = { ...filters }

            // Remove empty filters
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === 'all') {
                    delete params[key]
                }
            })

            const response = await api.get('/rendezvous/export', { params })
            const data = response.data.data

            if (!data || data.length === 0) {
                toast.error(t('common.noData'))
                return
            }

            const ws = XLSX.utils.json_to_sheet(data)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, "Rendezvous")
            XLSX.writeFile(wb, `rendezvous_export_${format(new Date(), 'yyyy-MM-dd')}.xlsx`)
            toast.success(t('common.success'))
        } catch (error) {
            console.error(error)
            toast.error(t('common.error'))
        } finally {
            setExporting(false)
        }
    }

    return (
        <div className="rdv-list-page">
            {/* Page Header */}
            <div className="page-header">
                <div>
                    <h1 className="page-title">{t('rendezvous.title')}</h1>
                    <p className="page-subtitle">{pagination.total} rendez-vous</p>
                </div>

                <div className="flex gap-2">
                    {hasPermission('EXPORT_EXCEL') && (
                        <button
                            className="btn btn-secondary"
                            onClick={handleExport}
                            disabled={exporting}
                        >
                            {exporting ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
                            Exporter
                        </button>
                    )}
                    {hasPermission('ADD_RDV') && (
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/rendezvous/add')}
                        >
                            <Plus size={18} />
                            {t('rendezvous.add')}
                        </button>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="card">
                <div className="filters-bar">
                    <form onSubmit={handleSearch} className="search-form">
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                className="form-input search-input"
                                placeholder={t('common.search') + '...'}
                                value={filters.search}
                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                            />
                        </div>
                    </form>

                    <div className="filters-actions">
                        <button
                            className={`btn btn-secondary ${showFilters ? 'active' : ''}`}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <Filter size={18} />
                            {t('common.filter')}
                        </button>
                    </div>
                </div>

                {showFilters && (
                    <div className="filters-panel">
                        <div className="filters-grid">
                            <div className="form-group">
                                <label className="form-label">{t('rendezvous.etat')}</label>
                                <select
                                    className="form-select"
                                    value={filters.etat}
                                    onChange={(e) => setFilters(prev => ({ ...prev, etat: e.target.value }))}
                                >
                                    <option value="all">{t('common.all')}</option>
                                    <option value="PLANIFIE">{t('rendezvous.states.PLANIFIE')}</option>
                                    <option value="QUALITE_DONE">{t('rendezvous.states.QUALITE_DONE')}</option>
                                    <option value="DISPATCHED">{t('rendezvous.states.DISPATCHED')}</option>
                                    <option value="CONSEILLER_DONE">{t('rendezvous.states.CONSEILLER_DONE')}</option>
                                    <option value="ANNULE">{t('rendezvous.states.ANNULE')}</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('rendezvous.date')} (début)</label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={filters.date_debut}
                                    onChange={(e) => setFilters(prev => ({ ...prev, date_debut: e.target.value }))}
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">{t('rendezvous.date')} (fin)</label>
                                <input
                                    type="date"
                                    className="form-input"
                                    value={filters.date_fin}
                                    onChange={(e) => setFilters(prev => ({ ...prev, date_fin: e.target.value }))}
                                />
                            </div>
                        </div>

                        <div className="filters-footer">
                            <button className="btn btn-ghost" onClick={clearFilters}>
                                <X size={16} />
                                Effacer les filtres
                            </button>
                            <button className="btn btn-primary" onClick={handleSearch}>
                                Appliquer
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Table */}
            <div className="card">
                {loading ? (
                    <div className="table-loading">
                        <Loader2 size={32} className="spin" />
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>{t('rendezvous.numero')}</th>
                                        <th>{t('rendezvous.equipe')}</th>
                                        <th>{t('rendezvous.agent')}</th>
                                        <th>{t('rendezvous.client')}</th>
                                        <th>{t('rendezvous.phone')}</th>
                                        <th>{t('rendezvous.city')}</th>
                                        <th>{t('rendezvous.date')}</th>
                                        <th>Date saisie</th>
                                        {hasPermission('VIEW_STATUS') && <th>{t('rendezvous.etat')}</th>}
                                        <th>{t('common.actions')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rendezvous.length === 0 ? (
                                        <tr>
                                            <td colSpan={hasPermission('VIEW_STATUS') ? 9 : 8} className="text-center text-muted p-6">
                                                {t('common.noData')}
                                            </td>
                                        </tr>
                                    ) : (
                                        rendezvous.map((rdv) => (
                                            <tr key={rdv._id}>
                                                <td>#{rdv.numeroSequentiel}</td>
                                                <td>{rdv.equipe_rdv?.nom_equipe || '-'}</td>
                                                <td>{rdv.agent_rdv?.prenom} {rdv.agent_rdv?.nom}</td>
                                                <td>
                                                    <strong>
                                                        {rdv.client_rdv?.prenom_client} {rdv.client_rdv?.nom_client}
                                                    </strong>
                                                </td>
                                                <td>{rdv.client_rdv?.natel_client}</td>
                                                <td>
                                                    {rdv.client_rdv?.adresse_client?.ville}
                                                    {rdv.client_rdv?.adresse_client?.npa && (
                                                        <span className="text-muted"> ({rdv.client_rdv?.adresse_client?.npa})</span>
                                                    )}
                                                </td>
                                                <td>{format(new Date(rdv.dateHeure_rdv), 'Pp', { locale: dateLocale })}</td>
                                                <td>{rdv.date_saisi_rdv ? format(new Date(rdv.date_saisi_rdv), 'P', { locale: dateLocale }) : '-'}</td>
                                                {hasPermission('VIEW_STATUS') && (
                                                    <td>
                                                        <span className={`badge ${getStateBadgeClass(rdv.etat_rdv)}`}>
                                                            {t(`rendezvous.states.${rdv.etat_rdv}`)}
                                                        </span>
                                                    </td>
                                                )}
                                                <td>
                                                    <div className="table-actions">
                                                        <button
                                                            className="btn btn-icon btn-ghost"
                                                            title={t('common.view')}
                                                            onClick={() => navigate(`/rendezvous/${rdv._id}`)}
                                                        >
                                                            <Eye size={16} />
                                                        </button>
                                                        {hasPermission('MODIF_RDV') && user?.profil?.code_profil !== 'AGENT' && (!['ANNULE', 'SIGNEE', 'ERREUR'].includes(rdv.etat_rdv) || user?.godMode) && (
                                                            <button
                                                                className="btn btn-icon btn-ghost"
                                                                title={t('common.edit')}
                                                                onClick={() => navigate(`/rendezvous/${rdv._id}/edit`)}
                                                            >
                                                                <Edit size={16} />
                                                            </button>
                                                        )}
                                                        {hasPermission('DELETE_RDV') && (
                                                            <button
                                                                className="btn btn-icon btn-ghost"
                                                                title={t('common.delete')}
                                                                onClick={() => handleDelete(rdv._id)}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {pagination.pages > 1 && (
                            <div className="table-pagination">
                                <span className="pagination-info">
                                    Page {pagination.page} sur {pagination.pages}
                                </span>
                                <div className="pagination">
                                    <button
                                        className="pagination-btn"
                                        disabled={pagination.page <= 1}
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                    >
                                        <ChevronLeft size={16} />
                                    </button>
                                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                        let pageNum
                                        if (pagination.pages <= 5) {
                                            pageNum = i + 1
                                        } else if (pagination.page <= 3) {
                                            pageNum = i + 1
                                        } else if (pagination.page >= pagination.pages - 2) {
                                            pageNum = pagination.pages - 4 + i
                                        } else {
                                            pageNum = pagination.page - 2 + i
                                        }
                                        return (
                                            <button
                                                key={pageNum}
                                                className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                                                onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                                            >
                                                {pageNum}
                                            </button>
                                        )
                                    })}
                                    <button
                                        className="pagination-btn"
                                        disabled={pagination.page >= pagination.pages}
                                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
